import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCanManageCompany } from "@/hooks/useCanManageCompany";

export interface PhaseRoleAssignment {
  id: string;
  appliance_type: string;
  phase_category: string;
  /** "company" = user_companies.role (role_value "admin") of the active company; "custom" = custom_roles.id. */
  role_type: "company" | "custom";
  role_value: string;
}

export function usePhaseRoles(applianceType: string | null) {
  const [phaseRoles, setPhaseRoles] = useState<PhaseRoleAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPhaseRoles = async () => {
    if (!applianceType) return;
    setIsLoading(true);
    const { data, error } = await supabase
      .from("workflow_phase_roles")
      .select("*")
      .eq("appliance_type", applianceType);
    if (!error && data) {
      setPhaseRoles(data as PhaseRoleAssignment[]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchPhaseRoles();
  }, [applianceType]);

  const getRolesForPhase = (phaseCategory: string): PhaseRoleAssignment[] => {
    return phaseRoles.filter((r) => r.phase_category === phaseCategory);
  };

  const addRoleToPhase = async (
    phaseCategory: string,
    roleType: "company" | "custom",
    roleValue: string
  ) => {
    if (!applianceType) return;
    const { data, error } = await supabase
      .from("workflow_phase_roles")
      .insert({
        appliance_type: applianceType,
        phase_category: phaseCategory,
        role_type: roleType,
        role_value: roleValue,
      })
      .select()
      .single();
    if (!error && data) {
      setPhaseRoles((prev) => [...prev, data as PhaseRoleAssignment]);
    }
    return { data, error };
  };

  const removeRoleFromPhase = async (id: string) => {
    const { error } = await supabase
      .from("workflow_phase_roles")
      .delete()
      .eq("id", id);
    if (!error) {
      setPhaseRoles((prev) => prev.filter((r) => r.id !== id));
    }
    return { error };
  };

  return { phaseRoles, isLoading, getRolesForPhase, addRoleToPhase, removeRoleFromPhase, refetch: fetchPhaseRoles };
}

/** Pure access rule, exported for tests. */
export function canAccessPhaseRule(
  rolesForPhase: Pick<PhaseRoleAssignment, "role_type" | "role_value">[],
  ctx: { isSuperadmin: boolean; isCompanyAdmin: boolean; customRoleIds: string[] },
): boolean {
  if (ctx.isSuperadmin) return true;
  if (rolesForPhase.length === 0) return true;
  return rolesForPhase.some(
    (pr) =>
      (pr.role_type === "company" && pr.role_value === "admin" && ctx.isCompanyAdmin) ||
      (pr.role_type === "custom" && ctx.customRoleIds.includes(pr.role_value)),
  );
}

/**
 * Check if the current user has access to a given phase based on role assignments.
 * If no roles are assigned to a phase, everyone has access. Superadmins bypass.
 */
export function usePhaseAccess(applianceType: string | null) {
  const { user } = useAuth();
  const { phaseRoles, isLoading: rolesLoading } = usePhaseRoles(applianceType);
  const { isSuperadmin, isCompanyAdmin, isLoading: gateLoading } = useCanManageCompany();
  const [userCustomRoleIds, setUserCustomRoleIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setUserCustomRoleIds([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    supabase.from("user_custom_roles").select("role_id").eq("user_id", user.id).then(({ data }) => {
      if (cancelled) return;
      setUserCustomRoleIds((data || []).map((r: any) => r.role_id));
      setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [user]);

  const canAccessPhase = (phaseCategory: string): boolean =>
    canAccessPhaseRule(
      phaseRoles.filter((r) => r.phase_category === phaseCategory),
      { isSuperadmin, isCompanyAdmin, customRoleIds: userCustomRoleIds },
    );

  /**
   * For sequential handoff: find which phases the user can work on,
   * considering that previous phases must be completed first.
   */
  const getAccessiblePhaseIndex = (
    phaseCategories: string[],
    completedPhases: Set<string>
  ): { accessible: Set<number>; currentHandoff: number } => {
    const accessible = new Set<number>();
    let currentHandoff = -1;

    for (let i = 0; i < phaseCategories.length; i++) {
      const cat = phaseCategories[i];
      const hasAccess = canAccessPhase(cat);
      
      // In sequential handoff: a phase is accessible if user has role AND all previous phases are complete
      const previousComplete = phaseCategories.slice(0, i).every((c) => completedPhases.has(c));
      
      if (hasAccess && previousComplete) {
        accessible.add(i);
        if (currentHandoff === -1) currentHandoff = i;
      }
    }

    return { accessible, currentHandoff };
  };

  return {
    canAccessPhase,
    getAccessiblePhaseIndex,
    isLoading: isLoading || rolesLoading || gateLoading,
    isSuperadmin,
    isCompanyAdmin,
    phaseRoles,
  };
}
